import React, { ReactElement, Suspense } from "react";
import { useRoutes } from "react-router-dom";

import Loading from "@/components/state-pages/Loading";
import { ROUTES } from "@/constants/routes_sidebar/routes-constant";
import { ProductDetailPage, ProductPage, Projects, RequestPage } from "@/pages";
import AssignTools from "@/pages/projects/assign-tools";
import AddReportFromRoster from "@/pages/reports/daily-reports/components/AddReportFromRoster";
import ViewAwaitingRequestProducts from "@/pages/tools/Request/components/awaiting/ViewAwaitingRequestProducts";
import ViewReturnRequestProducts from "@/pages/tools/Request/components/return-request/ViewReturnRequestProducts";
import ViewTransferRequestProducts from "@/pages/tools/Request/components/transfer-request/ViewTransferRequestProducts";

// lazy import routes
const AddReport = React.lazy(
  () => import("@/pages/reports/daily-reports/components/AddReport"),
);
const AnalyticsSummary = React.lazy(
  () => import("@/pages/analytics/summary/AnalyticsSummary"),
);
const Case2 = React.lazy(() => import("@/pages/payroll/case-2"));
const ContractInvoicePage = React.lazy(
  () => import("@/pages/invoicing/contract-invoice"),
);
const Customer = React.lazy(() => import("@/pages/clients/customer"));
const DailyReport = React.lazy(() => import("@/pages/reports/daily-reports"));
const DocketInvoicePage = React.lazy(
  () => import("@/pages/invoicing/docket-invoice"),
);
const LoadTypePage = React.lazy(
  () => import("@/pages/waste-management/load-type"),
);
const PR_Detail = React.lazy(
  () => import("@/pages/project-register/PR_Detail"),
);
const Part1 = React.lazy(() => import("@/pages/payroll/part-1"));
const ProjectRegister = React.lazy(() => import("@/pages/project-register"));
const ProspectComp = React.lazy(() => import("@/pages/clients/prospect"));
const SubContractorTimeSheet = React.lazy(
  () => import("@/pages/time-tracking/sub-contractor"),
);
const TaskAndClaims = React.lazy(() => import("@/pages/task-and-claims"));
const TimeSheet = React.lazy(() => import("@/pages/time-tracking/time-sheet"));
const TimeTrackingSetting = React.lazy(
  () => import("@/pages/time-tracking/settings"),
);
const UpdateReport = React.lazy(
  () => import("@/pages/reports/daily-reports/components/UpdateReport"),
);
const VehiclesPage = React.lazy(
  () => import("@/pages/waste-management/vehicles"),
);
const WareHousingDetailsPage = React.lazy(
  () => import("@/pages/ware-housing/ware-house-detail"),
);
const WareHousingPage = React.lazy(() => import("@/pages/ware-housing"));
const WasteSettings = React.lazy(
  () => import("@/pages/waste-management/settings"),
);
const WasteSummary = React.lazy(
  () => import("@/pages/waste-management/summary"),
);
const WasteTypeComp = React.lazy(
  () => import("@/pages/waste-management/waste-type"),
);
const WeighBridge = React.lazy(
  () => import("@/pages/waste-management/weigh-bridge"),
);
const WeightScale = React.lazy(
  () => import("@/pages/waste-management/weight-scale"),
);
const WorkDocketWrapper = React.lazy(
  () => import("@/pages/reports/work-docket/context/WorkDocketWrapper"),
);

const DocumentParser = React.lazy(() => import("@/pages/documentParser"));
const BrowseFile = React.lazy(
  () => import("@/pages/documentParser/BrowseFile"),
);
const ViewUploadedFile = React.lazy(
  () => import("@/pages/documentParser/ViewUploadedFile"),
);
const XeroInvoicesPage = React.lazy(
  () => import("@/pages/invoicing/xero-invoice"),
);
const Drives = React.lazy(() => import("@/pages/task-and-claims/drive"));

const Activity = React.lazy(() => import("@/pages/activity"));

const UnderConstructing = React.lazy(
  () => import("@/components/state-pages/UnderConstructing"),
);

export const CustomerRoutes = (): ReactElement => {
  const routes = useRoutes([
    { path: `${ROUTES.CUSTOMER}`, element: <Customer /> },
    { path: `${ROUTES.PROSPECT}`, element: <ProspectComp /> },
  ]) as ReactElement;

  return routes;
};

export const InvoicingRoutes = (): ReactElement => {
  const routes = useRoutes([
    { path: `${ROUTES.CONTRACT_INVOICE}`, element: <ContractInvoicePage /> },
    { path: `${ROUTES.DOCKET_INVOICE}`, element: <DocketInvoicePage /> },
    { path: `${ROUTES.XERO_INVOICE}`, element: <XeroInvoicesPage /> },
  ]) as ReactElement;

  return routes;
};

export const WasteRoutes = (): ReactElement => {
  const routes = useRoutes([
    { path: `${ROUTES.SUMMARY}`, element: <WasteSummary /> },
    { path: `${ROUTES.WEIGHT_SCALE}`, element: <WeightScale /> },
    { path: `${ROUTES.WEIGH_BRIDGE}`, element: <WeighBridge /> },
    {
      path: `${ROUTES.WEIGHTING_REPORTS}`,
      element: (
        <Suspense fallback={<Loading />}>
          <UnderConstructing title='Weighting Reports' />
        </Suspense>
      ),
    },
    { path: `${ROUTES.VEHICLES}`, element: <VehiclesPage /> },
    { path: `${ROUTES.LOAD_TYPE}`, element: <LoadTypePage /> },
    { path: `${ROUTES.WASTE_TYPE}`, element: <WasteTypeComp /> },
    {
      path: `${ROUTES.DAILY_REPORTS}`,
      element: (
        <Suspense fallback={<Loading />}>
          <UnderConstructing title='Daily Reports' />
        </Suspense>
      ),
    },
    { path: `${ROUTES.SETTINGS}`, element: <WasteSettings /> },
  ]) as ReactElement;

  return routes;
};

export const ReportRoutes = (): ReactElement => {
  const routes = useRoutes([
    { path: `${ROUTES.DAILY_REPORTS}`, element: <DailyReport /> },
    { path: `${ROUTES.DAILY_REPORTS}/form`, element: <AddReport /> },
    {
      path: `${ROUTES.DAILY_REPORTS}/:id`,
      element: <AddReportFromRoster />,
    },
    { path: `${ROUTES.DAILY_REPORTS}/edit/:id`, element: <UpdateReport /> },
    { path: `${ROUTES.WORK_DOCKET}`, element: <WorkDocketWrapper /> },
  ]) as ReactElement;

  return routes;
};

export const TimeTrackingRoutes = (): ReactElement => {
  const routes = useRoutes([
    {
      path: `${ROUTES.SUMMARY}`,
      element: (
        <Suspense fallback={<Loading />}>
          <UnderConstructing title='Time Tracking Summary' />
        </Suspense>
      ),
    },
    {
      path: `${ROUTES.TIME_SHEET}`,
      element: <TimeSheet />,
    },
    {
      path: `${ROUTES.SUB_CONTRACTOR}`,
      element: <SubContractorTimeSheet />,
    },
    {
      path: `${ROUTES.WORK_LOG}`,
      element: (
        <Suspense fallback={<Loading />}>
          <UnderConstructing title='Work Log' />
        </Suspense>
      ),
    },
    {
      path: `${ROUTES.SETTINGS}`,
      element: <TimeTrackingSetting />,
    },
  ]) as ReactElement;

  return routes;
};

export const PayrollRoutes = (): ReactElement => {
  const routes = useRoutes([
    { path: `${ROUTES.CASE_2}`, element: <Case2 /> },
    {
      path: `${ROUTES.PART_1}`,
      element: <Part1 />,
    },
  ]) as ReactElement;

  return routes;
};

export const WareHousingRoutes = (): ReactElement => {
  const routes = useRoutes([
    { path: `/`, element: <WareHousingPage /> },
    {
      path: `/:id`,
      element: <WareHousingDetailsPage />,
    },
  ]) as ReactElement;

  return routes;
};

export const ProjectRoutes = (): ReactElement => {
  const routes = useRoutes([
    { path: `/`, element: <Projects /> },
    {
      path: `/:id`,
      element: <AssignTools />,
    },
  ]) as ReactElement;

  return routes;
};

export const ProjectRegisterRoutes = (): ReactElement => {
  const routes = useRoutes([
    { path: `/`, element: <ProjectRegister /> },
    {
      path: `/:id`,
      element: <PR_Detail />,
    },
  ]) as ReactElement;

  return routes;
};

export const ActivityRoutes = (): ReactElement => {
  const routes = useRoutes([
    { path: `/`, element: <Activity /> },
  ]) as ReactElement;

  return routes;
};

export const AnalyticsRoute = (): ReactElement => {
  const routes = useRoutes([
    { path: `${ROUTES.SUMMARY}`, element: <AnalyticsSummary /> },
  ]) as ReactElement;

  return routes;
};
export const DocumentRoute = (): ReactElement => {
  const routes = useRoutes([
    { path: "/", element: <DocumentParser /> },
    { path: ROUTES.BROWSE_FILE, element: <BrowseFile /> },
    { path: ROUTES.VIEW_FILE, element: <ViewUploadedFile /> },
  ]) as ReactElement;

  return routes;
};

export const TaskAndCLaimRoutes = (): ReactElement => {
  const routes = useRoutes([
    { path: "/", element: <TaskAndClaims /> },
    { path: `${ROUTES.DRIVES}/:projectId/:leadId`, element: <Drives /> },
  ]) as ReactElement;

  return routes;
};

export const ToolsRoutes = (): ReactElement => {
  const routes = useRoutes([
    { path: `/${ROUTES.PRODUCTS}`, element: <ProductPage /> },
    { path: `/${ROUTES.PRODUCTS}/:id`, element: <ProductDetailPage /> },
    { path: `/${ROUTES.REQUEST}`, element: <RequestPage /> },
    {
      path: `/${ROUTES.TRANSFER_REQUEST}/:id`,
      element: <ViewTransferRequestProducts />,
    },
    {
      path: `/${ROUTES.RETURN_REQUEST}/:id`,
      element: <ViewReturnRequestProducts />,
    },
    {
      path: `/${ROUTES.AWAITING}/:id`,
      element: <ViewAwaitingRequestProducts />,
    },
  ]) as ReactElement;

  return routes;
};
